import { Badge, Button, Flex, Skeleton, Table, Text, TextField } from '@radix-ui/themes'
import { ChevronRightIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { Link } from 'react-router-dom'
import {
  PagePamPlayerSearchSummary,
  PamPlayerSearchSummary,
} from '@Generated/back-office-client/src'
import { Routes } from '@Components/main-app/types'
import { Pagination } from '@Components/common/pagination'
import { LoadingRows } from '@Components/util/loading-rows'
import { getAccountStatusDisplay } from '@Components/pam-verification/pam-player-status-display'
import { PamVerificationStatusBadge } from '@Components/pam-verification/pam-verification-status-badge'

type PlayerSearchListViewProps = {
  page?: PagePamPlayerSearchSummary
  search: string
  minSearchLength: number
  pageSize: number
  currentPage: number
  currentSearchHref: string
  isLoading: boolean
  onSearchChange: (search: string) => void
  onPageChange: (page: number) => void
  onRowClick: (row: PamPlayerSearchSummary) => void
}

const formatName = (row: PamPlayerSearchSummary): string => {
  const name = [row.firstName, row.lastName].filter((part) => Boolean(part)).join(' ')
  return name || '—'
}

export const PlayerSearchListView = ({
  page,
  search,
  minSearchLength,
  pageSize,
  currentPage,
  currentSearchHref,
  isLoading,
  onSearchChange,
  onPageChange,
  onRowClick,
}: PlayerSearchListViewProps) => {
  const rows = page?.results.map((row) => {
    const accountDisplay = getAccountStatusDisplay(row.status)
    return (
      <Table.Row key={row.lookupId} onClick={() => onRowClick(row)} className="player-search-row">
        <Table.Cell>{formatName(row)}</Table.Cell>
        <Table.Cell>{row.email ?? '—'}</Table.Cell>
        <Table.Cell>
          <Badge color={accountDisplay.color} variant="soft">
            {accountDisplay.label}
          </Badge>
        </Table.Cell>
        <Table.Cell>
          {row.verificationStatus ? (
            <PamVerificationStatusBadge status={row.verificationStatus} />
          ) : (
            <Text>—</Text>
          )}
        </Table.Cell>
        <Table.Cell>
          <Button asChild size="1" variant="soft" color="gray" onClick={(e) => e.stopPropagation()}>
            <Link
              to={Routes.PLAYER_DETAILS.replace(':lookupId', row.lookupId)}
              state={{ from: currentSearchHref }}>
              View
              <ChevronRightIcon />
            </Link>
          </Button>
        </Table.Cell>
      </Table.Row>
    )
  })

  const trimmedSearchLength = search.trim().length
  const showSearchHint = trimmedSearchLength > 0 && trimmedSearchLength < minSearchLength
  const hasSearched = isLoading || page !== undefined
  const isEmpty = !isLoading && page !== undefined && page.results.length === 0

  return (
    <Flex direction="column" gap="4">
      <TextField.Root
        placeholder="Search by name, email, or id"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}>
        <TextField.Slot>
          <MagnifyingGlassIcon height="16" width="16" />
        </TextField.Slot>
      </TextField.Root>

      {showSearchHint && (
        <Text size="1" color="gray">
          Type at least {minSearchLength} characters to search.
        </Text>
      )}

      {hasSearched ? (
        <Skeleton loading={isLoading}>
          <Table.Root variant="surface" className="player-search-table">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Verification</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>{isLoading ? <LoadingRows /> : rows}</Table.Body>
          </Table.Root>
        </Skeleton>
      ) : (
        <Flex align="center" justify="center" p="6">
          <Text color="gray">Search by name, email, or id to find a player.</Text>
        </Flex>
      )}

      {isEmpty && <Text color="gray">No players match this search.</Text>}

      {!isLoading && page !== undefined && (
        <Pagination
          totalItems={page.totalResults}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={onPageChange}
        />
      )}
    </Flex>
  )
}
